import { getAccessToken } from '@teko/auth';
import { Flex, Image, LazyImage, Loading, styled } from '@teko/ds-desktop';
import { CloseOutlined } from '@teko/ds-icon';
import { ProductVideo, SOURCE_VIDEO } from '@teko/teko-ecommerce';
import UploadFile from 'assets/upload-file.svg';
import { UPLOAD_IMAGE_PATHNAME, UPLOAD_VIDEO_PATHNAME } from 'constants/index';
import { useTenantConfig } from 'contexts';
import Upload, { UploadProps } from 'rc-upload';
import { RcFile } from 'rc-upload/lib/interface';
import { PropsWithChildren } from 'react';

export interface IMediaUploaderProps extends PropsWithChildren, UploadProps {
  fileList?: UploadFileResponse[];
  maxItems?: number;
  onDelete: (fileUrl: string) => void;
}

export interface UploadFileResponse {
  url: string;
  type: string;
  status: string;
  file: RcFile;
  image_url?: string;
}

export const MediaUploader: React.FC<IMediaUploaderProps> = ({
  children,
  fileList,
  maxItems = 5,
  onDelete,
  ...restProps
}) => {
  const { getProcessEnv, isMobile } = useTenantConfig();
  const baseFileServiceUrl = getProcessEnv('BASE_FILE_SERVICE_API_URL');

  const uploaderProps: UploadProps = {
    action: (file: RcFile) => {
      if (file.type.includes('image'))
        return `${baseFileServiceUrl}${UPLOAD_IMAGE_PATHNAME}`;
      return `${baseFileServiceUrl}${UPLOAD_VIDEO_PATHNAME}`;
    },
    name: 'file',
    accept: 'image/*,video/*',
    multiple: true,
    headers: { Authorization: `Bearer ${getAccessToken()}` },
  };

  const isExceededMaxItems =
    typeof maxItems === 'number' &&
    !!fileList?.length &&
    fileList.length >= maxItems;

  return (
    <>
      {/* Danh sách file */}
      <Flex gap={isMobile ? 's4' : 's12'} align="start" flexWrap="wrap">
        {fileList?.map((item, index) => (
          <ItemsWrapper key={index} isMobile={isMobile}>
            {item.type === 'image' && (
              <Image
                src={item?.image_url || item?.url || ''}
                style={{
                  borderRadius: 6,
                  cursor: 'pointer',
                  width: '100%',
                  height: isMobile ? '55px' : '60px',
                }}
                objectFit="cover"
              />
            )}
            {item.type === 'video' && (
              <ProductVideo
                url={item.url}
                source={SOURCE_VIDEO.SYSTEM}
                videoProps={{
                  width: '100%',
                  height: '100%',
                  preload: 'none',
                  playsInline: true,
                  autoPlay: true,
                  muted: true,
                  controls: isMobile ? false : true,
                }}
              />
            )}
            {item.status === 'done' && (
              <div
                style={{
                  position: 'absolute',
                  top: '1px',
                  right: '4px',
                }}
                onClick={() => onDelete(item.url)}
              >
                <CloseOutlined size={16} color="#A7ABC3" />
              </div>
            )}
            {item.status === 'uploading' && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(0,0,0,0.4)',
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 6,
                }}
              >
                <Loading isLoading size={24} />
              </div>
            )}
          </ItemsWrapper>
        ))}
        {!isExceededMaxItems && (
          <Upload {...uploaderProps} {...restProps}>
            {!!fileList?.length ? (
              <ItemsWrapper
                style={{ display: 'flex', alignItems: 'center' }}
                isMobile={isMobile}
              >
                <LazyImage src={UploadFile} imageWidth={48} />
              </ItemsWrapper>
            ) : (
              children
            )}
          </Upload>
        )}
      </Flex>
    </>
  );
};

const ItemsWrapper = styled.div(
  ({ isMobile }: { isMobile: boolean }) => `
  position: relative;
  width: ${isMobile ? '55px' : '60px'};
  height: ${isMobile ? '55px' : '60px'};
  border-radius: 6px;
  border: 1px solid #d9d9d9;
`,
);
